import { createPortal } from "react-dom";
import { getThemeClasses } from "../../utils/ThemeClasses";
import { platformLabelOf } from "../../features/dashboards/reportingConfig";
import type { DeviceMovement } from "../../features/dashboards/queries";

export default function DeviceMovementsModal({
  themeClasses,
  title,
  devices,
  loading = false,
  showType = true,
  showPlatform = true,
  onClose,
}: {
  themeClasses: ReturnType<typeof getThemeClasses>;
  title: string;
  devices: DeviceMovement[];
  loading?: boolean;
  showType?: boolean;
  showPlatform?: boolean;
  onClose: () => void;
}) {
  const typeBadge = (t: string) => {
    const isAlta = t === "alta";
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs font-medium ${isAlta ? "text-green-500" : "text-red-500"}`}
      >
        <i className={isAlta ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
        {isAlta ? "Alta" : "Baja"}
      </span>
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`flex items-center justify-between p-4 border-b ${themeClasses.cardBorder}`}
        >
          <h3 className={`text-base sm:text-lg font-bold ${themeClasses.text}`}>
            {title} ({loading ? "…" : devices.length})
          </h3>
          <button type="button" onClick={onClose} className={themeClasses.textSecondary}>
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div className="overflow-auto p-4">
          {loading ? (
            <div className="space-y-3">
              <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700/40 rounded animate-pulse"></div>
            </div>
          ) : devices.length === 0 ? (
            <p className={`text-sm ${themeClasses.textSecondary}`}>Sin equipos.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${themeClasses.cardBorder}`}>
                  {showType && (
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Tipo</th>
                  )}
                  <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>IMEI</th>
                  <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Nombre</th>
                  {showPlatform && (
                    <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Plataforma</th>
                  )}
                  <th className={`text-left py-2 px-3 ${themeClasses.textSecondary}`}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d, i) => (
                  <tr
                    key={`${d.identifier}-${i}`}
                    className={`border-b ${themeClasses.cardBorder}`}
                  >
                    {showType && <td className="py-2 px-3">{typeBadge(d.type)}</td>}
                    <td className={`py-2 px-3 ${themeClasses.textSecondary}`}>{d.identifier || "—"}</td>
                    <td className={`py-2 px-3 ${themeClasses.text}`}>{d.name || "—"}</td>
                    {showPlatform && (
                      <td className={`py-2 px-3 ${themeClasses.text}`}>{platformLabelOf(d.platform)}</td>
                    )}
                    <td className={`py-2 px-3 ${themeClasses.textSecondary}`}>{d.status_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
